import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Divider,
  useTheme,
  FormControlLabel,
  Switch,
  Autocomplete
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import {
  FileDownload,
  Refresh,
  FilterList,
  GetApp
} from '@mui/icons-material';
import { useImportExport } from '../context';
import { ExportSectionProps } from '../types/ComponentTypes';
import { EntityFilter } from '../apiService/importExportApiService';
import Dealer from 'app/apiService/dealer/dealerApiService';
import { useSelector } from 'react-redux';
import { selectDealerSelected, selectDealersList } from 'app/pages/Header/selectors';
import { 
  selectDealerSelected as selectDealerSelectedAIAvatar,
  selectDealersList as selectDealersListAIAvatar
} from 'app/NewAIAvatarLayout/redux/headerSlice/selectors';

const ExportSection: React.FC<ExportSectionProps> = ({
  config,
  onExport,
  onError,
  loading = false,
  onOpenDownloadModal,
  isInAIAvatarLayout = false
}) => {
  const theme = useTheme();
  const { 
    handleExport, 
    exportLoading, 
    exportError,
    exportData,
    exportFilename,
    availableEntities,
    selectedEntity,
    entityFilters,
    entitiesLoading,
    entitiesError,
    loadEntities,
    selectEntity
  } = useImportExport();

  // Get selected dealer from Redux store
  const selectedDealerRegular = useSelector(selectDealerSelected);
  const selectedDealerAIAvatar = useSelector(selectDealerSelectedAIAvatar);
  const selectedDealer = isInAIAvatarLayout ? selectedDealerAIAvatar : selectedDealerRegular;
  const selectedDealerId = selectedDealer?.dealer || selectedDealer?._id || '';
  
  // Get dealers list from Redux store
  const dealersListRegular = useSelector(selectDealersList);
  const dealersListAIAvatar = useSelector(selectDealersListAIAvatar);
  const dealersListFromRedux = isInAIAvatarLayout ? dealersListAIAvatar : dealersListRegular;
  
  // Export form state
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'zip'>('zip');
  const [exportLimit, setExportLimit] = useState<string>('all');
  const [customLimit, setCustomLimit] = useState<string>('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  
  // Filter state
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [showFilters, setShowFilters] = useState(true);
  
  // DataGridMAIQ style filter state
  const [filterItems, setFilterItems] = useState<any[]>([]);
  const linkOperator = 'and'; // Always use AND operator
  
  // Date filters
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  
  // Export completion state
  const [exportCompleted, setExportCompleted] = useState(false);

  // Dealers state for DEALER_ID_FROM_UI filter
  const [dealers, setDealers] = useState<any[]>([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [dealersError, setDealersError] = useState<string | null>(null);

  // Load entities on component mount
  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  // Reset export format if it's JSON (since we're hiding it)
  useEffect(() => {
    const availableFormats = config.supportedFormats.filter(f => f !== 'json');
    const hasCurrentFormat = availableFormats.includes(exportFormat);

    if (availableFormats.length === 0) {
      setExportFormat('zip');
      return;
    }

    if (exportFormat === 'json' || !hasCurrentFormat) {
      // Prefer ZIP if available, otherwise use first available format
      const preferredFormat = availableFormats.includes('zip') ? 'zip' : (availableFormats[0] as 'csv' | 'zip');
      setExportFormat(preferredFormat);
    }
  }, [exportFormat, config.supportedFormats]);

  // Fetch dealers when entity filters contain DEALER_ID_FROM_UI
  useEffect(() => {
    const hasDealerFilter = entityFilters.some(
      filter => filter.defaultValue === 'DEALER_ID_FROM_UI'
    );
    
    if (!hasDealerFilter) return;
    
    // If in AIAvatarLayout, try to get dealers from Redux first
    if (isInAIAvatarLayout && dealersListFromRedux?.data?.body?.items?.length > 0) {
      const dealersFromRedux = dealersListFromRedux.data.body.items;
      if (dealersFromRedux.length > 0 && dealers.length === 0) {
        setDealers(dealersFromRedux);
        setDealersLoading(false);
        setDealersError(null);
        return;
      }
    }
    
    // For regular layout or if Redux doesn't have dealers, fetch from API
    if (dealers.length === 0 && !dealersLoading) {
      const fetchDealers = async () => {
        try {
          setDealersLoading(true);
          setDealersError(null);
          
          const response = await Dealer.getAllDealersForDataGrid({
            limit: 1000, // Fetch a large number of dealers
            skip: 0
          });
          
          let dealersList: any[] = [];
          
          // Handle different response structures
          if (response.body?.data?.items && Array.isArray(response.body.data.items)) {
            dealersList = response.body.data.items;
          } else if (response.body?.data && Array.isArray(response.body.data)) {
            dealersList = response.body.data;
          } else if (response.body?.items && Array.isArray(response.body.items)) {
            dealersList = response.body.items;
          } else if (Array.isArray(response.body)) {
            dealersList = response.body;
          }
          
          if (dealersList.length > 0) {
            // Sort dealers: active first, then alphabetically
            const sortedDealers = dealersList
              .sort((a: any, b: any) => {
                if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') {
                  return 1;
                } else if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') {
                  return -1;
                } else {
                  return (a.name || '').toLowerCase() > (b.name || '').toLowerCase() ? 1 : -1;
                }
              });
            setDealers(sortedDealers);
          } else {
            setDealers([]);
          }
        } catch (error: any) {
          setDealersError(error.message || 'Failed to load dealers');
          setDealers([]);
        } finally {
          setDealersLoading(false);
        }
      };
      
      fetchDealers();
    }
  }, [entityFilters, dealers.length, dealersLoading, isInAIAvatarLayout, dealersListFromRedux]);

  // Set default dealer from Redux when entity filters are loaded and dealer filter exists
  useEffect(() => {
    if (entityFilters && entityFilters.length > 0 && selectedDealerId) {
      const dealerFilter = entityFilters.find(
        filter => filter.defaultValue === 'DEALER_ID_FROM_UI'
      );
      
      if (dealerFilter && (!filters[dealerFilter.field] || filters[dealerFilter.field] === '')) {
        // Set the selected dealer as default if not already set
        setFilters(prev => ({
          ...prev,
          [dealerFilter.field]: selectedDealerId
        }));
        
        // Also add to filter items for DataGridMAIQ (dealer field uses 'equals' operator)
        setFilterItems(prev => {
          // Remove existing filter item for this field if any
          const filtered = prev.filter(item => item.columnField !== dealerFilter.field);
          // Add new filter item
          return [...filtered, {
            columnField: dealerFilter.field,
            operatorValue: 'equals',
            value: selectedDealerId
          }];
        });
      }
    }
  }, [entityFilters, selectedDealerId, filters]);

  // Debug export data changes - only log when data actually changes
  useEffect(() => {
    if (exportData && exportData instanceof Blob) {
      // Silent - no console logs
    }
  }, [exportData]);

  // Helper function to format entity name: replace underscores with spaces and capitalize each word
  const formatEntityName = (entityName: string): string => {
    return entityName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to get selected dealer name from filters
  const getSelectedDealerName = (): string => {
    // Find dealer filter field
    const dealerFilter = entityFilters.find(filter => filter.defaultValue === 'DEALER_ID_FROM_UI');
    if (!dealerFilter) return '';
    
    // Get selected dealer ID from filters
    const selectedDealerIdFromFilter = filters[dealerFilter.field] || '';
    if (!selectedDealerIdFromFilter) return '';
    
    // Find dealer in dealers list
    const selectedDealer = dealers.find(dealer => dealer._id === selectedDealerIdFromFilter);
    if (!selectedDealer) return '';
    
    // Return dealer name (sanitize for filename - remove special characters, convert to lowercase)
    const dealerName = selectedDealer.name || selectedDealer.site_title || '';
    // Replace spaces and special characters with underscores, convert to lowercase
    return dealerName.toLowerCase().replace(/[^a-zA-Z0-9]/g, '_');
  };

  const sanitizeForFilename = (value: any): string => {
    if (value === undefined || value === null) return '';
    const normalizedValue = String(value).trim();
    if (!normalizedValue) return '';
    return normalizedValue
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
  };

  const getFilterValueLabel = (filter: EntityFilter, value: any): string => {
    if (filter.defaultValue === 'DEALER_ID_FROM_UI') {
      return getSelectedDealerName();
    }

    if (filter.type === 'date' && typeof value === 'string') {
      const date = DateTime.fromISO(value);
      if (date.isValid) {
        return sanitizeForFilename(date.toFormat('yyyy_MM_dd'));
      }
    }

    if (filter.type === 'select' && Array.isArray(filter.enumValues)) {
      const matchedOption = filter.enumValues.find(option => {
        if (typeof option === 'object' && option !== null) {
          return String(option.value ?? '') === String(value ?? '');
        }
        return String(option) === String(value ?? '');
      });

      if (matchedOption && typeof matchedOption === 'object' && matchedOption !== null) {
        return sanitizeForFilename(matchedOption.label ?? matchedOption.value ?? value);
      }
    }

    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }

    return sanitizeForFilename(value);
  };

  const buildExportFilename = (): string => {
    const extension = exportFormat;
    const entityName = sanitizeForFilename(selectedEntity?.entity || 'export');
    const dateStr = new Date().toISOString().split('T')[0];
    const dealerName = getSelectedDealerName();

    const selectedFilterValues = entityFilters
      .map(filter => ({ filter, value: filters[filter.field] }))
      .filter(({ value }) => value !== undefined && value !== null && value !== '')
      .filter(({ filter }) => filter.defaultValue !== 'DEALER_ID_FROM_UI')
      .map(({ filter, value }) => getFilterValueLabel(filter, value))
      .filter(Boolean);

    const uniqueFilterValues = Array.from(new Set(selectedFilterValues));
    const filenameParts = [
      dealerName || entityName,
      ...(dealerName ? [entityName] : []),
      ...uniqueFilterValues
    ].filter(Boolean);

    const baseName = filenameParts.length > 0
      ? filenameParts.join('_')
      : `${entityName}_export`;

    return `${baseName}_${dateStr}.${extension}`;
  };

  // Handle export completion - only run when export is completed and data is available
  useEffect(() => {
    if (exportCompleted && exportData && exportData instanceof Blob) {
      const blobUrl = URL.createObjectURL(exportData);
      
      if (onOpenDownloadModal) {
        let fileName = buildExportFilename();
        if (!fileName && exportFilename) {
          fileName = exportFilename;
        }
        
        const title = `Export ${selectedEntity?.entity ? formatEntityName(selectedEntity.entity) : 'Data'}`;
        onOpenDownloadModal(blobUrl, title, fileName);
        setExportCompleted(false); // Reset state
      }
    }
  }, [exportCompleted, exportData, exportFilename, selectedEntity?.entity, exportFormat, onOpenDownloadModal, filters, dealers, entityFilters]);

  // Get appropriate operator for field type
  const getOperatorForField = (field: string, value: any): string => {
    if (field.includes('date')) {
      if (field.includes('start')) {
        return 'after';
      } else if (field.includes('end')) {
        return 'before';
      }
      return 'is';
    }
    
    if (typeof value === 'boolean') {
      return 'equals'; // For boolean fields, use 'equals' operator
    }
    
    if (typeof value === 'number') {
      return 'equals';
    }
    
    // For text fields, check if it's a select field
    if (entityFilters) {
      const filterConfig = entityFilters.find(f => f.field === field);
      if (filterConfig?.type === 'select') {
        return 'equals';
      }
    }
    
    return 'contains';
  };

  // Initialize filters from selected entity
  useEffect(() => {
    if (entityFilters && entityFilters.length > 0) {
      const initialFilters: Record<string, any> = {};
      const initialFilterItems: any[] = [];
      
      entityFilters.forEach(filter => {
        // Check if defaultValue is "DEALER_ID_FROM_UI" - use selected dealer from header
        if (filter.defaultValue === 'DEALER_ID_FROM_UI') {
          // Use selected dealer ID from header if available
          const dealerId = selectedDealerId || '';
          initialFilters[filter.field] = dealerId;
          
          // Add to DataGridMAIQ filter items if dealer is selected
          if (dealerId && dealerId !== '') {
            const operatorValue = getOperatorForField(filter.field, dealerId);
            initialFilterItems.push({
              columnField: filter.field,
              operatorValue: operatorValue,
              value: dealerId
            });
          }
        } else {
          // Use defaultValue from API if available, otherwise use empty string
          const defaultValue = filter.defaultValue !== undefined ? filter.defaultValue : '';
          initialFilters[filter.field] = defaultValue;
          
          // Add to DataGridMAIQ filter items if there's a default value
          if (defaultValue !== '' && defaultValue !== null && defaultValue !== undefined) {
            const operatorValue = getOperatorForField(filter.field, defaultValue);
            initialFilterItems.push({
              columnField: filter.field,
              operatorValue: operatorValue,
              value: defaultValue
            });
          }
        }
      });
      
      setFilters(initialFilters);
      setFilterItems(initialFilterItems);
    }
  }, [entityFilters, selectedDealerId]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update DataGridMAIQ style filter items
    updateFilterItems(field, value);
  };

  // Update filter items in DataGridMAIQ format
  const updateFilterItems = (field: string, value: any) => {
    setFilterItems(prev => {
      // Remove existing filter for this field
      const filtered = prev.filter(item => item.columnField !== field);
      
      // For boolean fields, always add filter (true/false)
      // For date fields, add if value exists and is not null and not empty string
      // For other fields, only add if value exists and is not empty
      const shouldAddFilter = typeof value === 'boolean' || 
                             (field.includes('date') && value && value !== null && value !== '') ||
                             (!field.includes('date') && value && value !== '' && value !== null);
      
      if (shouldAddFilter) {
        const operatorValue = getOperatorForField(field, value);
        const newFilter = {
          columnField: field,
          operatorValue: operatorValue,
          value: value
        };
        const updatedItems = [...filtered, newFilter];
        return updatedItems;
      }
      
      return filtered;
    });
  };

  // Helper function to safely convert DateTime to ISO string
  const safeToISOString = (date: DateTime | null): string | null => {
    if (!date) {
      return null;
    }
    
    // Check if it's a Luxon DateTime object
    if (typeof date === 'object' && 'isValid' in date) {
      if (!date.isValid) {
        return null;
      }
      try {
        // Use toISO() method for Luxon DateTime and convert to UTC to remove timezone offset
        const isoString = date.toUTC().toISO();
        return isoString;
      } catch (error) {
        return null;
      }
    }
    
    // If it's already a string, return as is
    if (typeof date === 'string') {
      return date;
    }
    
    return null;
  };

  const handleExportClick = async () => {
    if (!selectedEntity) {
      onError(new Error('Please select an entity type first'));
      return;
    }

    try {
      setExportCompleted(false);
      // Create DataGridMAIQ style filter model
      const filterModel = {
        items: filterItems,
        linkOperator: linkOperator
      };
      
      // Create export parameters
      const exportParams: Record<string, any> = {
        format: exportFormat,
        filters: filterModel, // Pass filters as DataGridMAIQ style object
        entityType: selectedEntity.entity // Use selected entity instead of config
      };
      
      // Add individual date filters for easier API handling
      if (startDate && startDate.isValid) {
        exportParams.start_date = safeToISOString(startDate);
      }
      
      if (endDate && endDate.isValid) {
        exportParams.end_date = safeToISOString(endDate);
      }
      
      // Add limit if specified
      if (exportLimit !== 'all') {
        exportParams.limit = exportLimit === 'custom' ? customLimit : exportLimit;
      }
      
      // Add selected fields
      if (selectedFields.length > 0) {
        exportParams.fields = selectedFields;
      }
      
      // Add dealer ID if available
      if (config.dealerId) {
        exportParams.dealer = config.dealerId;
      }
      
      // Call export API first to get the blob data
      await handleExport(exportParams);
      
      // Mark export as completed - the useEffect will handle the rest
      setExportCompleted(true);
      
    } catch (error) {
      onError(error);
    }
  };

  const handleResetFilters = () => {
    setFilters({});
    setFilterItems([]);
    setStartDate(null);
    setEndDate(null);
    setExportLimit('all');
    setCustomLimit('');
    setSelectedFields([]);
    
    // Reset filters based on selected entity with default values
    if (entityFilters && entityFilters.length > 0) {
      const initialFilters: Record<string, any> = {};
      const initialFilterItems: any[] = [];
      
      entityFilters.forEach(filter => {
        // Check if defaultValue is "DEALER_ID_FROM_UI" - use selected dealer from header
        if (filter.defaultValue === 'DEALER_ID_FROM_UI') {
          // Use selected dealer ID from header if available
          const dealerId = selectedDealerId || '';
          initialFilters[filter.field] = dealerId;
          
          // Add to DataGridMAIQ filter items if dealer is selected
          if (dealerId && dealerId !== '') {
            const operatorValue = getOperatorForField(filter.field, dealerId);
            initialFilterItems.push({
              columnField: filter.field,
              operatorValue: operatorValue,
              value: dealerId
            });
          }
        } else {
          const defaultValue = filter.defaultValue !== undefined ? filter.defaultValue : '';
          initialFilters[filter.field] = defaultValue;
          
          // Add to DataGridMAIQ filter items if there's a default value
          if (defaultValue !== '' && defaultValue !== null && defaultValue !== undefined) {
            const operatorValue = getOperatorForField(filter.field, defaultValue);
            initialFilterItems.push({
              columnField: filter.field,
              operatorValue: operatorValue,
              value: defaultValue
            });
          }
        }
      });
      
      setFilters(initialFilters);
      setFilterItems(initialFilterItems);
    }
  };

  const renderFilterField = (filter: EntityFilter) => {
    // For DEALER_ID_FROM_UI, don't use defaultValue, use empty string if not set
    const defaultValue = filter.defaultValue === 'DEALER_ID_FROM_UI' ? '' : (filter.defaultValue !== undefined ? filter.defaultValue : '');
    const value = filters[filter.field] !== undefined ? filters[filter.field] : defaultValue;
    
    switch (filter.type) {
      case 'date':
        return (
          <DatePicker
            label={filter.label}
            value={filter.field.includes('start') ? startDate : endDate}
            onChange={(newValue) => {
              try {
                if (filter.field.includes('start')) {
                  setStartDate(newValue);
                } else {
                  setEndDate(newValue);
                }
                
                // Safely handle date conversion with proper null checks
                const dateValue = safeToISOString(newValue);
                
                // Always call handleFilterChange - let it handle null/empty values
                handleFilterChange(filter.field, dateValue);
                
              } catch (error) {
                // Reset the date if there's an error
                if (filter.field.includes('start')) {
                  setStartDate(null);
                } else {
                  setEndDate(null);
                }
                // Call handleFilterChange with null to remove the filter
                handleFilterChange(filter.field, null);
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: filter.placeholder,
                size: 'small',
                error: false // Ensure no error state from parent
              }
            }}
            // Add additional props for better error handling
            disableFuture={false}
            disablePast={false}
            format="MM/dd/yyyy"
          />
        );
        
      case 'select':
        // Check if this is a dealer filter (defaultValue === "DEALER_ID_FROM_UI")
        if (filter.defaultValue === 'DEALER_ID_FROM_UI') {
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{filter.label}</InputLabel>
              <Select
                value={value || ''}
                label={filter.label}
                onChange={(e) => {
                  // Pass the dealer's _id to the filter
                  handleFilterChange(filter.field, e.target.value);
                }}
                disabled={dealersLoading}
              >
                <MenuItem value="">
                  <em>Select {filter.label}</em>
                </MenuItem>
                {dealersLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading dealers...
                  </MenuItem>
                ) : dealersError ? (
                  <MenuItem disabled>
                    Error loading dealers
                  </MenuItem>
                ) : (
                  dealers.map((dealer) => (
                    <MenuItem key={dealer._id} value={dealer._id}>
                      {dealer.name || dealer.site_title || dealer._id}
                      {dealer.status && dealer.status !== 'ACTIVE' && ` (${dealer.status})`}
                    </MenuItem>
                  ))
                )}
              </Select>
              {dealersError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {dealersError}
                </Typography>
              )}
            </FormControl>
          );
        }
        
        // Regular select field
        const selectOptions = Array.isArray(filter.enumValues) ? filter.enumValues : [];
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{filter.label}</InputLabel>
            <Select
              value={value ?? ''}
              label={filter.label}
              onChange={(e) => handleFilterChange(filter.field, e.target.value)}
            >
              <MenuItem value="">
                <em>Select {filter.label}</em>
              </MenuItem>
              {selectOptions.map((option, index) => {
                const isObjectOption = typeof option === 'object' && option !== null;
                const objectOption = isObjectOption
                  ? (option as { label?: string; value?: string | number })
                  : null;
                const optionValue: string | number = objectOption?.value ?? (
                  typeof option === 'string' || typeof option === 'number' ? option : ''
                );
                const optionLabel = objectOption?.label ?? String(optionValue);
                const optionKey = `${String(optionValue)}-${index}`;

                return (
                  <MenuItem key={optionKey} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
        
      case 'boolean':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {filter.label}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(value)}
                  onChange={(e) => handleFilterChange(filter.field, e.target.checked)}
                  color="primary"
                />
              }
              label={Boolean(value) ? 'Yes' : 'No'}
              labelPlacement="start"
            />
          </Box>
        );
        
      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={filter.label}
            value={value}
            onChange={(e) => handleFilterChange(filter.field, e.target.value)}
            placeholder={filter.placeholder}
          />
        );
        
      default:
        // Check if this is a dealer filter (defaultValue === "DEALER_ID_FROM_UI")
        // Even if type is not 'select', we should show dropdown for DEALER_ID_FROM_UI
        if (filter.defaultValue === 'DEALER_ID_FROM_UI') {
          return (
            <FormControl fullWidth size="small">
              <InputLabel>{filter.label}</InputLabel>
              <Select
                value={value || ''}
                label={filter.label}
                onChange={(e) => {
                  // Pass the dealer's _id to the filter
                  handleFilterChange(filter.field, e.target.value);
                }}
                disabled={dealersLoading}
              >
                <MenuItem value="">
                  <em>Select {filter.label}</em>
                </MenuItem>
                {dealersLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading dealers...
                  </MenuItem>
                ) : dealersError ? (
                  <MenuItem disabled>
                    Error loading dealers
                  </MenuItem>
                ) : (
                  dealers.map((dealer) => (
                    <MenuItem key={dealer._id} value={dealer._id}>
                      {dealer.name || dealer.site_title || dealer._id}
                      {dealer.status && dealer.status !== 'ACTIVE' && ` (${dealer.status})`}
                    </MenuItem>
                  ))
                )}
              </Select>
              {dealersError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {dealersError}
                </Typography>
              )}
            </FormControl>
          );
        }
        
        return (
          <TextField
            fullWidth
            size="small"
            label={filter.label}
            value={value}
            onChange={(e) => handleFilterChange(filter.field, e.target.value)}
            placeholder={filter.placeholder}
          />
        );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box sx={{ p: 2.5, width: '100%' }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            color: '#0F2A52',
            fontWeight: 600,
            fontSize: '1.1rem',
            mb: 1
          }}
        >
          Export Data
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 3,
            fontSize: '0.85rem',
            lineHeight: 1.5
          }}
        >
          Select an entity type and export your data in various formats with dynamic filters.
        </Typography>

        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ width: '100%' }}>
          {/* Entity Selection */}
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Select Entity Type
                </Typography>
                
                {entitiesError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {entitiesError}
                  </Alert>
                )}
                
                <FormControl fullWidth size="small">
                  <InputLabel>Choose Entity</InputLabel>
                  <Select
                    value={selectedEntity?.entity || ''}
                    label="Choose Entity"
                    onChange={(e) => {
                      const entity = availableEntities.find(ent => ent.entity === e.target.value);
                      if (entity) {
                        selectEntity(entity);
                      }
                    }}
                    disabled={entitiesLoading}
                  >
                    {entitiesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading entities...
                      </MenuItem>
                    ) : (
                      availableEntities.map((entity) => (
                        <MenuItem key={entity.entity} value={entity.entity}>
                          {formatEntityName(entity.entity)}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                
                {selectedEntity && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Selected: {formatEntityName(selectedEntity.entity)} 
                    {entityFilters.length > 0 && ` (${entityFilters.length} filters available)`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Export Configuration */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Export Configuration
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Export Format</InputLabel>
                      <Select
                        value={exportFormat}
                        label="Export Format"
                        onChange={(e) => setExportFormat(e.target.value as any)}
                      >
                        {config.supportedFormats
                          .filter((format) => format !== 'json') // Hide JSON option
                          .map((format) => (
                            <MenuItem key={format} value={format}>
                              {format.toUpperCase()}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Record Limit</InputLabel>
                      <Select
                        value={exportLimit}
                        label="Record Limit"
                        onChange={(e) => setExportLimit(e.target.value)}
                      >
                        <MenuItem value="all">All Records</MenuItem>
                        <MenuItem value="100">100 Records</MenuItem>
                        <MenuItem value="500">500 Records</MenuItem>
                        <MenuItem value="1000">1,000 Records</MenuItem>
                        <MenuItem value="5000">5,000 Records</MenuItem>
                        <MenuItem value="custom">Custom Limit</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {exportLimit === 'custom' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Custom Limit"
                        value={customLimit}
                        onChange={(e) => setCustomLimit(e.target.value)}
                        placeholder="Enter number of records"
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Filters */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography 
                    variant="h6"
                    sx={{
                      color: '#0F2A52',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    Filters
                  </Typography>
                  <Button
                    startIcon={<FilterList />}
                    onClick={() => setShowFilters(!showFilters)}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: isInAIAvatarLayout
                        ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                        : '#0F2A52',
                      color: isInAIAvatarLayout
                        ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                        : '#0F2A52',
                      '&:hover': {
                        borderColor: isInAIAvatarLayout
                          ? (theme.palette.mode === 'light' ? '#333333' : '#e0e0e0')
                          : '#0A1F3F',
                        backgroundColor: isInAIAvatarLayout
                          ? (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)')
                          : 'rgba(15, 42, 82, 0.04)',
                      },
                    }}
                  >
                    {showFilters ? 'Hide' : 'Show'} Filters
                  </Button>
                </Box>
                
                {showFilters && entityFilters && entityFilters.length > 0 && (
                  <Grid container spacing={2}>
                    {entityFilters.map((filter) => (
                      <Grid item xs={12} key={filter.field}>
                        {renderFilterField(filter)}
                      </Grid>
                    ))}
                    
                    <Grid item xs={12}>
                      <Button
                        onClick={handleResetFilters}
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh />}
                        sx={{
                          borderColor: '#0F2A52',
                          color: '#0F2A52',
                          '&:hover': {
                            borderColor: '#0A1F3F',
                            backgroundColor: 'rgba(15, 42, 82, 0.04)',
                          },
                        }}
                      >
                        Reset Filters
                      </Button>
                    </Grid>
                  </Grid>
                )}
                
                {(!showFilters || !entityFilters || entityFilters.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedEntity ? 'No filters available for this entity type.' : 'Please select an entity type to see available filters.'}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Field Selection */}
          {config.exportFields && config.exportFields.length > 0 && (
            <Grid item xs={12}>
              <Card 
                elevation={0}
                sx={{
                  borderRadius: 1.5,
                  boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                  border: '1px solid rgba(15, 40, 76, 0.08)',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      color: '#0F2A52',
                      fontWeight: 600,
                      fontSize: '1rem',
                      mb: 2
                    }}
                  >
                    Field Selection
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select which fields to include in the export. Leave empty to include all fields.
                  </Typography>
                  
                  <Autocomplete
                    multiple
                    options={config.exportFields}
                    value={selectedFields}
                    onChange={(event, newValue) => setSelectedFields(newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select fields to export"
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Export Button */}
          <Grid item xs={12}>
            <Paper 
              sx={{ 
                p: 2.5, 
                backgroundColor: 'background.paper',
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      color: '#0F2A52',
                      fontWeight: 600,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    Ready to Export
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      fontSize: '0.85rem',
                      lineHeight: 1.5
                    }}
                  >
                    Click the export button to download your data with the selected configuration.
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  size="large"
                  startIcon={exportLoading ? <CircularProgress size={20} /> : <FileDownload />}
                  onClick={handleExportClick}
                  disabled={exportLoading || loading || !selectedEntity}
                  sx={{ 
                    minWidth: 150,
                    backgroundColor: isInAIAvatarLayout
                      ? (theme.palette.mode === 'light' ? '#000000' : '#ffffff')
                      : '#0F2A52',
                    color: isInAIAvatarLayout
                      ? (theme.palette.mode === 'light' ? '#ffffff' : '#000000')
                      : 'white',
                    '&:hover': {
                      backgroundColor: isInAIAvatarLayout
                        ? (theme.palette.mode === 'light' ? '#333333' : '#e0e0e0')
                        : '#0A1F3F',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(15, 42, 82, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                >
                  {exportLoading ? 'Exporting...' : selectedEntity ? 'Export Data' : 'Select Entity First'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ExportSection;
